/**
 * Periodic terms for the Moon, from Jean Meeus, Astronomical Algorithms (2nd ed., 1998).
 *
 * `LUNAR_LONGITUDE_DISTANCE` is Table 47.A and `LUNAR_LATITUDE` Table 47.B: each row is the
 * multipliers of the arguments D, M, M′ and F, then the coefficient of the sine (longitude,
 * in millionths of a degree; latitude likewise) and of the cosine (distance, in metres
 * after dividing by 1000 for kilometres). Terms with a nonzero M multiply by E to the
 * power |M|, as the book prescribes.
 */

/** [D, M, M′, F, Σl (10⁻⁶ °), Σr (10⁻³ km)] */
export const LUNAR_LONGITUDE_DISTANCE: readonly (readonly number[])[] = [
  [0, 0, 1, 0, 6_288_774, -20_905_355],
  [2, 0, -1, 0, 1_274_027, -3_699_111],
  [2, 0, 0, 0, 658_314, -2_955_968],
  [0, 0, 2, 0, 213_618, -569_925],
  [0, 1, 0, 0, -185_116, 48_888],
  [0, 0, 0, 2, -114_332, -3149],
  [2, 0, -2, 0, 58_793, 246_158],
  [2, -1, -1, 0, 57_066, -152_138],
  [2, 0, 1, 0, 53_322, -170_733],
  [2, -1, 0, 0, 45_758, -204_586],
  [0, 1, -1, 0, -40_923, -129_620],
  [1, 0, 0, 0, -34_720, 108_743],
  [0, 1, 1, 0, -30_383, 104_755],
  [2, 0, 0, -2, 15_327, 10_321],
  [0, 0, 1, 2, -12_528, 0],
  [0, 0, 1, -2, 10_980, 79_661],
  [4, 0, -1, 0, 10_675, -34_782],
  [0, 0, 3, 0, 10_034, -23_210],
  [4, 0, -2, 0, 8548, -21_636],
  [2, 1, -1, 0, -7888, 24_208],
  [2, 1, 0, 0, -6766, 30_824],
  [1, 0, -1, 0, -5163, -8379],
  [1, 1, 0, 0, 4987, -16_675],
  [2, -1, 1, 0, 4036, -12_831],
  [2, 0, 2, 0, 3994, -10_445],
  [4, 0, 0, 0, 3861, -11_650],
  [2, 0, -3, 0, 3665, 14_403],
  [0, 1, -2, 0, -2689, -7003],
  [2, 0, -1, 2, -2602, 0],
  [2, -1, -2, 0, 2390, 10_056],
  [1, 0, 1, 0, -2348, 6322],
  [2, -2, 0, 0, 2236, -9884],
  [0, 1, 2, 0, -2120, 5751],
  [0, 2, 0, 0, -2069, 0],
  [2, -2, -1, 0, 2048, -4950],
  [2, 0, 1, -2, -1773, 4130],
  [2, 0, 0, 2, -1595, 0],
  [4, -1, -1, 0, 1215, -3958],
  [0, 0, 2, 2, -1110, 0],
  [3, 0, -1, 0, -892, 3258],
  [2, 1, 1, 0, -810, 2616],
  [4, -1, -2, 0, 759, -1897],
  [0, 2, -1, 0, -713, -2117],
  [2, 2, -1, 0, -700, 2354],
  [2, 1, -2, 0, 691, 0],
  [2, -1, 0, -2, 596, 0],
  [4, 0, 1, 0, 549, -1423],
  [0, 0, 4, 0, 537, -1117],
  [4, -1, 0, 0, 520, -1571],
  [1, 0, -2, 0, -487, -1739],
  [2, 1, 0, -2, -399, 0],
  [0, 0, 2, -2, -381, -4421],
  [1, 1, 1, 0, 351, 0],
  [3, 0, -2, 0, -340, 0],
  [4, 0, -3, 0, 330, 0],
  [2, -1, 2, 0, 327, 0],
  [0, 2, 1, 0, -323, 1165],
  [1, 1, -1, 0, 299, 0],
  [2, 0, 3, 0, 294, 0],
  [2, 0, -1, -2, 0, 8752],
];

/** [D, M, M′, F, Σb (10⁻⁶ °)] */
export const LUNAR_LATITUDE: readonly (readonly number[])[] = [
  [0, 0, 0, 1, 5_128_122],
  [0, 0, 1, 1, 280_602],
  [0, 0, 1, -1, 277_693],
  [2, 0, 0, -1, 173_237],
  [2, 0, -1, 1, 55_413],
  [2, 0, -1, -1, 46_271],
  [2, 0, 0, 1, 32_573],
  [0, 0, 2, 1, 17_198],
  [2, 0, 1, -1, 9266],
  [0, 0, 2, -1, 8822],
  [2, -1, 0, -1, 8216],
  [2, 0, -2, -1, 4324],
  [2, 0, 1, 1, 4200],
  [2, 1, 0, -1, -3359],
  [2, -1, -1, 1, 2463],
  [2, -1, 0, 1, 2211],
  [2, -1, -1, -1, 2065],
  [0, 1, -1, -1, -1870],
  [4, 0, -1, -1, 1828],
  [0, 1, 0, 1, -1794],
  [0, 0, 0, 3, -1749],
  [0, 1, -1, 1, -1565],
  [1, 0, 0, 1, -1491],
  [0, 1, 1, 1, -1475],
  [0, 1, 1, -1, -1410],
  [0, 1, 0, -1, -1344],
  [1, 0, 0, -1, -1335],
  [0, 0, 3, 1, 1107],
  [4, 0, 0, -1, 1021],
  [4, 0, -1, 1, 833],
  [0, 0, 1, -3, 777],
  [4, 0, -2, 1, 671],
  [2, 0, 0, -3, 607],
  [2, 0, 2, -1, 596],
  [2, -1, 1, -1, 491],
  [2, 0, -2, 1, -451],
  [0, 0, 3, -1, 439],
  [2, 0, 2, 1, 422],
  [2, 0, -3, -1, 421],
  [2, 1, -1, 1, -366],
  [2, 1, 0, 1, -351],
  [4, 0, 0, 1, 331],
  [2, -1, 1, 1, 315],
  [2, -2, 0, -1, 302],
  [0, 0, 1, 3, -283],
  [2, 1, 1, -1, -229],
  [1, 1, 0, -1, 223],
  [1, 1, 0, 1, 223],
  [0, 1, -2, -1, -220],
  [2, 1, -1, -1, -220],
  [1, 0, 1, 1, -185],
  [2, -1, -2, -1, 181],
  [0, 1, 2, 1, -177],
  [4, 0, -2, -1, 176],
  [4, -1, -1, -1, 166],
  [1, 0, 1, -1, -164],
  [4, 0, 1, -1, 132],
  [1, 0, -1, -1, -119],
  [4, -1, 0, -1, 115],
  [2, -2, 0, 1, 107],
];

/**
 * Chapter 49: the periodic terms of the times of new moon, full moon and the quarters,
 * in days. Each row is [M multiplier, M′ multiplier, F multiplier, Ω multiplier, new moon,
 * full moon]; the E factor is applied as E to the power |M|.
 */
export const NEW_FULL_TERMS: readonly (readonly number[])[] = [
  [0, 1, 0, 0, -0.4072, -0.406_14],
  [1, 0, 0, 0, 0.172_41, 0.173_02],
  [0, 2, 0, 0, 0.016_08, 0.016_14],
  [0, 0, 2, 0, 0.010_39, 0.010_43],
  [-1, 1, 0, 0, 0.007_39, 0.007_34],
  [1, 1, 0, 0, -0.005_14, -0.005_15],
  [2, 0, 0, 0, 0.002_08, 0.002_09],
  [0, 1, -2, 0, -0.001_11, -0.001_11],
  [0, 1, 2, 0, -0.000_57, -0.000_57],
  [1, 2, 0, 0, 0.000_56, 0.000_56],
  [0, 3, 0, 0, -0.000_42, -0.000_42],
  [1, 0, 2, 0, 0.000_42, 0.000_42],
  [1, 0, -2, 0, 0.000_38, 0.000_38],
  [-1, 2, 0, 0, -0.000_24, -0.000_24],
  [0, 0, 0, 1, -0.000_17, -0.000_17],
  [2, 1, 0, 0, -0.000_07, -0.000_07],
  [0, 2, -2, 0, 0.000_04, 0.000_04],
  [3, 0, 0, 0, 0.000_04, 0.000_04],
  [1, 1, -2, 0, 0.000_03, 0.000_03],
  [0, 2, 2, 0, 0.000_03, 0.000_03],
  [1, 1, 2, 0, -0.000_03, -0.000_03],
  [-1, 1, 2, 0, 0.000_03, 0.000_03],
  [-1, 1, -2, 0, -0.000_02, -0.000_02],
  [1, 3, 0, 0, -0.000_02, -0.000_02],
  [0, 4, 0, 0, 0.000_02, 0.000_02],
];

/** The same for first and last quarter: [M, M′, F, Ω, coefficient]. */
export const QUARTER_TERMS: readonly (readonly number[])[] = [
  [0, 1, 0, 0, -0.628_01],
  [1, 0, 0, 0, 0.171_72],
  [1, 1, 0, 0, -0.011_83],
  [0, 2, 0, 0, 0.008_62],
  [0, 0, 2, 0, 0.008_04],
  [-1, 1, 0, 0, 0.004_54],
  [2, 0, 0, 0, 0.002_04],
  [0, 1, -2, 0, -0.0018],
  [0, 1, 2, 0, -0.0007],
  [0, 3, 0, 0, -0.0004],
  [-1, 2, 0, 0, -0.000_34],
  [1, 0, 2, 0, 0.000_32],
  [1, 0, -2, 0, 0.000_32],
  [2, 1, 0, 0, -0.000_28],
  [1, 2, 0, 0, 0.000_27],
  [0, 0, 0, 1, -0.000_17],
  [-1, 1, -2, 0, -0.000_05],
  [0, 2, 2, 0, 0.000_04],
  [1, 1, 2, 0, -0.000_04],
  [-2, 1, 0, 0, 0.000_04],
  [1, 1, -2, 0, 0.000_03],
  [3, 0, 0, 0, 0.000_03],
  [0, 2, -2, 0, 0.000_02],
  [-1, 1, 2, 0, 0.000_02],
  [1, 3, 0, 0, -0.000_02],
];
